import { useEffect, useMemo, useRef, useState } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { api } from "../api";
import SetupCard from "../components/SetupCard";
import FilterDrawer, { DEFAULT_FILTERS, type Filters } from "../components/FilterDrawer";

const PAGE_SIZE = 60;

function parseFilters(sp: URLSearchParams): Filters {
  const csv = (k: string) =>
    (sp.get(k) ?? "").split(",").map((s) => s.trim()).filter(Boolean);
  // origin defaults to manual when nothing in URL — matches the recent
  // Streamlit default at app.py:271.
  const originRaw = sp.get("origin");
  const origins = originRaw === null ? DEFAULT_FILTERS.origins : originRaw.split(",").filter(Boolean);
  return {
    symbols: csv("symbol"),
    timeframes: csv("tf"),
    directions: csv("direction"),
    origins,
    min_score: Number(sp.get("min_score") ?? 0) || 0,
    tradable: sp.get("tradable") === "1",
    tight: sp.get("tight") === "1",
  };
}

function filtersToParams(f: Filters): Record<string, string> {
  const out: Record<string, string> = {};
  if (f.symbols.length) out.symbol = f.symbols.join(",");
  if (f.timeframes.length) out.tf = f.timeframes.join(",");
  if (f.directions.length) out.direction = f.directions.join(",");
  // origin is special: empty array means "all", but we want to round-trip
  // an explicit empty so users can clear the manual default.
  out.origin = f.origins.join(",");
  if (f.min_score) out.min_score = String(f.min_score);
  if (f.tradable) out.tradable = "1";
  if (f.tight) out.tight = "1";
  return out;
}

function activeCount(f: Filters): number {
  let n = 0;
  if (f.symbols.length) n++;
  if (f.timeframes.length) n++;
  if (f.directions.length) n++;
  // origin always meaningful (default = manual); only count if user
  // has changed it from the default.
  if (
    f.origins.length !== DEFAULT_FILTERS.origins.length ||
    f.origins.some((o, i) => o !== DEFAULT_FILTERS.origins[i])
  )
    n++;
  if (f.min_score) n++;
  if (f.tradable) n++;
  if (f.tight) n++;
  return n;
}

export default function ListView() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const filters = useMemo(() => parseFilters(searchParams), [searchParams]);

  const apiParams = useMemo(() => {
    const f = filters;
    const p: Record<string, string | number | boolean> = { limit: PAGE_SIZE };
    if (f.symbols.length) p.symbol = f.symbols.join(",");
    if (f.timeframes.length) p.tf = f.timeframes.join(",");
    if (f.directions.length) p.direction = f.directions.join(",");
    if (f.origins.length) p.origin = f.origins.join(",");
    if (f.min_score) p.min_score = f.min_score;
    if (f.tradable) p.tradable = true;
    if (f.tight) p.tight = true;
    return p;
  }, [filters]);

  const {
    data,
    isLoading,
    error,
    isFetching,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ["setups", apiParams],
    queryFn: ({ pageParam }) =>
      api.setups({ ...apiParams, offset: pageParam as number }),
    initialPageParam: 0,
    getNextPageParam: (last) => {
      const next = last.offset + last.setups.length;
      return next < last.total ? next : undefined;
    },
  });

  const setups = useMemo(
    () => (data?.pages ?? []).flatMap((p) => p.setups),
    [data],
  );
  const total = data?.pages[0]?.total ?? 0;
  const facets = data?.pages[0]?.facets;

  // Auto-load the next page when the sentinel element scrolls into view.
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || !hasNextPage) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { rootMargin: "400px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage, setups.length]);

  const count = activeCount(filters);

  return (
    <div>
      <div className="list-header">
        <h2>Setups</h2>
        {data && (
          <span className="muted small">
            {setups.length} / {total}
            {isFetching && !isFetchingNextPage ? " · refreshing" : ""}
          </span>
        )}
        <button
          className="filter-btn"
          onClick={() => setDrawerOpen(true)}
          aria-label="filters"
        >
          Filters
          {count > 0 && <span className="filter-badge">{count}</span>}
        </button>
      </div>

      {isLoading && <p className="muted">Loading…</p>}
      {error && <p className="muted">Failed to load setups.</p>}
      {data && setups.length === 0 && (
        <p className="muted">No setups match the current filters.</p>
      )}
      {setups.length > 0 && (
        <div className="cards">
          {setups.map((s, i) => (
            <SetupCard key={(s.setup_id as string) ?? `${s.symbol}-${s.timeframe}-${i}`} s={s} />
          ))}
        </div>
      )}
      <div ref={sentinelRef} style={{ height: 1 }} />
      {isFetchingNextPage && (
        <p className="muted" style={{ textAlign: "center", padding: "12px 0" }}>
          Loading more…
        </p>
      )}

      <FilterDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        facets={facets}
        value={filters}
        onChange={(next) => {
          setSearchParams(filtersToParams(next), { replace: true });
        }}
      />
    </div>
  );
}
